/**
 * Listed / Proposed / Finalized deal money display helpers.
 */

export function finiteNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function formatDealMoney(value) {
  const n = finiteNumber(value, NaN);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
}

export function formatDealRatePerSqft(value) {
  const n = finiteNumber(value, NaN);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  })}`;
}

/** Display label: sq/ft or sq/mtr (project stores sq/mt). */
export function formatRateAreaUnit(raw) {
  const u = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '');
  if (
    u === 'sq/mt' ||
    u === 'sqmt' ||
    u === 'sqm' ||
    u === 'sq/mtr' ||
    u === 'sqmtr' ||
    u === 'squaremeter' ||
    u === 'squaremetre'
  ) {
    return 'sq/mtr';
  }
  if (u === 'sq/hectare' || u === 'sq/acres' || u === 'sq/gunta' || u === 'sq/cents') {
    return String(raw);
  }
  return 'sq/ft';
}

export function resolveDealRateAreaUnit(compare, deal, fallback) {
  return (
    compare?.rate_area_unit ||
    deal?.unit?.project?.measurement_type ||
    fallback ||
    'sq/ft'
  );
}

export function ratePerAreaLabel(unit) {
  return `Rate per ${formatRateAreaUnit(unit)}`;
}

export function ratePerAreaColumnHeader(unit) {
  return `Rate / ${formatRateAreaUnit(unit)} (Listed → Final)`;
}

/**
 * Compact cell: show Finalized, and Listed above it when different.
 * Optional Proposed in the middle when present and different from both.
 */
export function formatPricingCompareCell({ listed, proposed, finalized } = {}, asRate = false) {
  const format = asRate ? formatDealRatePerSqft : formatDealMoney;
  const listedN = listed == null ? null : asRate ? finiteNumber(listed) : Math.round(finiteNumber(listed));
  const proposedN = proposed == null ? null : asRate ? finiteNumber(proposed) : Math.round(finiteNumber(proposed));
  const finalizedN =
    finalized == null ? null : asRate ? finiteNumber(finalized) : Math.round(finiteNumber(finalized));

  if (finalizedN == null && listedN == null && proposedN == null) return '—';

  const lines = [];
  if (listedN != null && listedN !== finalizedN) {
    lines.push({ label: 'Listed', value: format(listedN) });
  }
  if (
    proposedN != null &&
    proposedN !== finalizedN &&
    proposedN !== listedN
  ) {
    lines.push({ label: 'Proposed', value: format(proposedN) });
  }
  lines.push({
    label: lines.length ? 'Final' : null,
    value: format(finalizedN != null ? finalizedN : listedN ?? proposedN)
  });
  return lines;
}

function saleableAreaFromUnit(unit) {
  const superA = finiteNumber(unit?.superBuiltUpArea);
  if (superA > 0) return superA;
  const accSuper = finiteNumber(unit?.accommodation?.superBuildUpArea);
  if (accSuper > 0) return accSuper;
  const build = finiteNumber(unit?.buildUpArea) || finiteNumber(unit?.accommodation?.buildUpArea);
  const loading = finiteNumber(unit?.loadingFactor);
  if (build > 0) return build * (1 + loading / 100);
  const plot = finiteNumber(unit?.accommodation?.plotArea);
  if (plot > 0) return plot;
  return Math.max(0, finiteNumber(unit?.area));
}

function ratePerSqft(price, area) {
  if (!(area > 0) || !(price >= 0)) return null;
  return Math.round((price / area) * 100) / 100;
}

function withRatePerSqft(compare, unit) {
  const rate_area_unit =
    compare?.rate_area_unit || unit?.project?.measurement_type || 'sq/ft';
  if (compare?.rate_per_sqft?.finalized != null || compare?.rate_per_sqft?.listed != null) {
    return { ...compare, rate_area_unit };
  }
  const area = finiteNumber(compare?.saleable_area) || saleableAreaFromUnit(unit);
  return {
    ...compare,
    saleable_area: area,
    rate_area_unit,
    rate_per_sqft: {
      listed: ratePerSqft(finiteNumber(compare?.listed?.unit_price), area),
      proposed: compare?.proposed
        ? ratePerSqft(finiteNumber(compare.proposed.unit_price), area)
        : null,
      finalized: ratePerSqft(finiteNumber(compare?.finalized?.unit_price), area)
    }
  };
}

export function getDealPricingCompare(deal, underConsideration = null) {
  if (deal?.pricing_compare) {
    if (!deal.pricing_compare.proposed && underConsideration) {
      return withRatePerSqft(
        {
          ...deal.pricing_compare,
          proposed: {
            unit_price: finiteNumber(underConsideration.customer_proposal),
            plc_price: finiteNumber(underConsideration.plc_price),
            gst: finiteNumber(underConsideration.gst),
            extra_charges: finiteNumber(underConsideration.extra_charges),
            total: finiteNumber(underConsideration.total_price)
          }
        },
        deal?.unit
      );
    }
    return withRatePerSqft(deal.pricing_compare, deal?.unit);
  }
  const listedUnit = finiteNumber(deal?.unit?.cost);
  const finalizedUnit = finiteNumber(deal?.unit_price);
  const discount = finiteNumber(deal?.discount);
  const listedFromDiscount =
    discount !== 0 ? finalizedUnit + discount : listedUnit || finalizedUnit;
  const listedPlc = finiteNumber(deal?.unit?.plc_price ?? deal?.plc_price);
  const listedGst = finiteNumber(deal?.unit?.gst ?? deal?.gst_price);
  const listedExtra = finiteNumber(deal?.unit?.extra_charges ?? deal?.extra_charges);
  const proposed = underConsideration
    ? {
        unit_price: finiteNumber(underConsideration.customer_proposal),
        plc_price: finiteNumber(underConsideration.plc_price),
        gst: finiteNumber(underConsideration.gst),
        extra_charges: finiteNumber(underConsideration.extra_charges),
        total: finiteNumber(underConsideration.total_price)
      }
    : null;
  const finalized = {
    unit_price: finalizedUnit,
    plc_price: finiteNumber(deal?.plc_price),
    gst: finiteNumber(deal?.gst_price),
    extra_charges: finiteNumber(deal?.extra_charges),
    total: finiteNumber(deal?.total_price)
  };
  const listed = {
    unit_price: listedFromDiscount,
    plc_price: listedPlc,
    gst: listedGst,
    extra_charges: listedExtra,
    total: listedFromDiscount + listedPlc + listedGst + listedExtra
  };
  return withRatePerSqft(
    {
      listed,
      proposed,
      finalized,
      unit_discount: Math.round(listed.unit_price - finalized.unit_price),
      total_discount: Math.round(listed.total - finalized.total)
    },
    deal?.unit
  );
}
